import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RoleSpecificSettings = ({ user, userProfile, onSuccess }) => {
  const isStudent = userProfile?.role === 'user';
  const isEducator = userProfile?.role === 'editor' || userProfile?.role === 'superuser';

  const [studentSettings, setStudentSettings] = useState({
    practiceReminders: true,
    progressSharing: false,
    dailyGoalTier: userProfile?.daily_goal_tier || 'casual'
  });

  const [educatorSettings, setEducatorSettings] = useState({
    teachingNotifications: true,
    studentCommunication: true,
    institutionalIntegration: false
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleStudentToggle = (key) => {
    setStudentSettings(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleEducatorToggle = (key) => {
    setEducatorSettings(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleGoalTierChange = (tier) => {
    setStudentSettings(prev => ({ ...prev, dailyGoalTier: tier }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    // Simulate save
    setTimeout(() => {
      setIsSaving(false);
      onSuccess?.('Preferences updated successfully!');
    }, 1000);
  };

  const goalTiers = [
    { value: 'casual', label: 'Casual', xp: 20, icon: 'Coffee' },
    { value: 'regular', label: 'Regular', xp: 40, icon: 'Target' },
    { value: 'serious', label: 'Serious', xp: 80, icon: 'Zap' },
    { value: 'intense', label: 'Intense', xp: 120, icon: 'Flame' }
  ];

  return (
    <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-bold text-white mb-2">
          Role-Specific Preferences
        </h2>
        <p className="text-blue-100">
          Customize settings based on your role
        </p>
      </div>

      {isStudent && (
        <>
          {/* Daily Goal Tier */}
          <div className="bg-white bg-opacity-5 rounded-xl p-6 space-y-4">
            <div className="flex items-center space-x-3">
              <Icon name="TrendingUp" size={24} className="text-green-400" />
              <div>
                <h3 className="text-lg font-semibold text-white">Daily Goal</h3>
                <p className="text-blue-200 text-sm">Set your daily XP target</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {goalTiers?.map((tier) => (
                <button
                  key={tier?.value}
                  onClick={() => handleGoalTierChange(tier?.value)}
                  className={`p-4 rounded-xl transition-all ${
                    studentSettings?.dailyGoalTier === tier?.value
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'bg-white bg-opacity-10 text-blue-100 hover:bg-opacity-20'
                  }`}
                >
                  <Icon name={tier?.icon} size={24} className="mx-auto mb-2" />
                  <p className="font-semibold">{tier?.label}</p>
                  <p className="text-sm opacity-80">{tier?.xp} XP/day</p>
                </button>
              ))}
            </div>
          </div>

          {/* Student Preferences */}
          <div className="bg-white bg-opacity-5 rounded-xl p-6 space-y-4">
            <div className="flex items-center space-x-3">
              <Icon name="Settings" size={24} className="text-blue-400" />
              <div>
                <h3 className="text-lg font-semibold text-white">Learning Preferences</h3>
                <p className="text-blue-200 text-sm">Customize your learning experience</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between py-3 border-b border-white border-opacity-10">
                <div>
                  <p className="text-white font-medium">Practice Reminders</p>
                  <p className="text-blue-200 text-sm">Get daily practice notifications</p>
                </div>
                <button
                  onClick={() => handleStudentToggle('practiceReminders')}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    studentSettings?.practiceReminders ? 'bg-purple-600' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      studentSettings?.practiceReminders ? 'translate-x-7' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="text-white font-medium">Progress Sharing</p>
                  <p className="text-blue-200 text-sm">Share progress with educators</p>
                </div>
                <button
                  onClick={() => handleStudentToggle('progressSharing')}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    studentSettings?.progressSharing ? 'bg-purple-600' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      studentSettings?.progressSharing ? 'translate-x-7' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {isEducator && (
        <div className="bg-white bg-opacity-5 rounded-xl p-6 space-y-4">
          <div className="flex items-center space-x-3">
            <Icon name="Users" size={24} className="text-orange-400" />
            <div>
              <h3 className="text-lg font-semibold text-white">Educator Preferences</h3>
              <p className="text-blue-200 text-sm">Manage teaching and communication settings</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between py-3 border-b border-white border-opacity-10">
              <div>
                <p className="text-white font-medium">Teaching Notifications</p>
                <p className="text-blue-200 text-sm">Receive updates about teaching content</p>
              </div>
              <button
                onClick={() => handleEducatorToggle('teachingNotifications')}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  educatorSettings?.teachingNotifications ? 'bg-purple-600' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    educatorSettings?.teachingNotifications ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-white border-opacity-10">
              <div>
                <p className="text-white font-medium">Student Communication</p>
                <p className="text-blue-200 text-sm">Allow students to contact you</p>
              </div>
              <button
                onClick={() => handleEducatorToggle('studentCommunication')}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  educatorSettings?.studentCommunication ? 'bg-purple-600' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    educatorSettings?.studentCommunication ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <p className="text-white font-medium">Institutional Integration</p>
                <p className="text-blue-200 text-sm">Connect with school systems</p>
              </div>
              <button
                onClick={() => handleEducatorToggle('institutionalIntegration')}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  educatorSettings?.institutionalIntegration ? 'bg-purple-600' : 'bg-gray-600'
                }`}
              >
                <span
                  className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    educatorSettings?.institutionalIntegration ? 'translate-x-7' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          variant="primary"
          disabled={isSaving}
          iconName="Save"
          iconPosition="left"
          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default RoleSpecificSettings;